import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { ReactNode, useState } from "react";
import {
  ParsedItemCell,
  PostHogInsightResponse,
} from "../../../../utils/analytics/types";
import { parseTable } from "../../../../utils/analytics/parsers";
import { analyticsTheme } from "../theme";
import { AnalyticsSectionCard } from "./AnalyticsSectionCard";
import { AnalyticsEmptyBlock } from "./AnalyticsEmptyBlock";

interface AnalyticsInsightTableProps {
  title: string;
  icon?: ReactNode;
  response?: PostHogInsightResponse;
  isLoading?: boolean;
  emptyLabel: string;
  testId?: string;
}

const isNumeric = (value: string) =>
  value.trim() !== "" && Number.isFinite(Number(value.replace(/[,%$]/g, "")));

const DealItemCell = ({ item }: { item: ParsedItemCell }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!item.imageSrc && !imageFailed;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minWidth: 0,
        maxWidth: 280,
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={item.imageSrc ?? undefined}
          alt=""
          onError={() => setImageFailed(true)}
          sx={{
            width: 44,
            height: 44,
            borderRadius: analyticsTheme.radius.sm,
            objectFit: "cover",
            flexShrink: 0,
            border: `1px solid ${analyticsTheme.border}`,
            backgroundColor: analyticsTheme.pageBg,
          }}
        />
      ) : (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: analyticsTheme.radius.sm,
            flexShrink: 0,
            backgroundColor: analyticsTheme.brandSoft,
            border: `1px solid ${analyticsTheme.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: analyticsTheme.brand,
          }}
          aria-hidden
        >
          <ImageNotSupportedOutlinedIcon sx={{ fontSize: 20, opacity: 0.7 }} />
        </Box>
      )}
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 600,
          color: analyticsTheme.text.primary,
          whiteSpace: "normal",
          lineHeight: 1.35,
        }}
      >
        {item.name}
      </Typography>
    </Box>
  );
};

export const AnalyticsInsightTable = ({
  title,
  icon,
  response,
  isLoading,
  emptyLabel,
  testId,
}: AnalyticsInsightTableProps) => {
  const table = parseTable(response);

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rounded"
              height={40}
              sx={{ borderRadius: analyticsTheme.radius.sm }}
            />
          ))}
        </Box>
      );
    }

    if (!table || table.rows.length === 0) {
      return <AnalyticsEmptyBlock label={emptyLabel} height={260} />;
    }

    return (
      <TableContainer sx={{ maxHeight: 380 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {table.headers.map((header, index) => (
                <TableCell
                  key={header}
                  align={index === 0 ? "left" : "right"}
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    color: analyticsTheme.text.muted,
                    backgroundColor: analyticsTheme.cardBg,
                    borderBottom: `1px solid ${analyticsTheme.borderStrong}`,
                    py: 1.25,
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  transition: analyticsTheme.transition,
                  "&:hover": { backgroundColor: analyticsTheme.brandSoft },
                  "&:last-of-type td": { borderBottom: 0 },
                }}
              >
                {row.map((cell, cellIndex) => {
                  const numeric = cellIndex !== 0 && isNumeric(cell);
                  const itemMeta =
                    cellIndex === 0 ? table.itemMetaByRow?.[rowIndex] : null;
                  return (
                    <TableCell
                      key={`${rowIndex}-${cellIndex}`}
                      align={cellIndex === 0 ? "left" : "right"}
                      sx={{
                        fontSize: 13.5,
                        py: 1.25,
                        color:
                          cellIndex === 0
                            ? analyticsTheme.text.primary
                            : analyticsTheme.text.secondary,
                        fontWeight: cellIndex === 0 ? 600 : 500,
                        fontVariantNumeric: numeric ? "tabular-nums" : "normal",
                        borderBottom: `1px solid ${analyticsTheme.border}`,
                        whiteSpace:
                          cellIndex === 0 && itemMeta ? "normal" : "nowrap",
                      }}
                    >
                      {itemMeta ? <DealItemCell item={itemMeta} /> : cell}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <AnalyticsSectionCard title={title} icon={icon} testId={testId}>
      {renderBody()}
    </AnalyticsSectionCard>
  );
};
